/**
 * Shared profitability assumptions editor — canonical zeecore /profitability/v2/config.
 * Used on Profitability Report (truth mode) and Fleet Intelligence truth report.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Settings, Save, X, CircleHelp } from 'lucide-react';
import { Button } from '../ui/button';
import { getCurrentUserEmail, readSessionUserEmail } from '../../auth/cognitoAuth';
import {
  fetchTruthProfitabilityConfig,
  saveTruthProfitabilityConfig,
} from '../../services/profitabilityTruthClient';
import { fetchFleetCostAssumptionsConfig } from '../../services/fleetCostAssumptionsClient';
import type { FleetCostAssumptionsConfig } from '../../types/fleetCostAssumptions';
import {
  PROFITABILITY_VEHICLE_TYPES,
  PROFITABILITY_TONNAGE_CLASSES,
  type ProfitabilityAssumptionsConfig,
  type ProfitabilityCostRateBasis,
  type ProfitabilityFuelSource,
  type ProfitabilityStandingCostPolicy,
  type ProfitabilityTonnageClass,
  type ProfitabilityVehicleType,
} from '../../types/profitabilityTruth';
import { countFleetCostQtyByName } from '../../utils/fleetCostAssumptionsQty';
import { fetchFleetCostAssetClassLabels } from '../../utils/fleetCostAssetLabels';
import { deriveStandingPerDayMaps } from '../../utils/fleetCostStandingPerDay';
import {
  countTrailerAssets,
  trailerStandingAddOnPerDayGbp,
  trailerStandingFromConfig,
  TRAILER_ALLOCATION_BODY_STYLE_PEER,
  withTrailerStandingAssetPrefill,
} from '../../utils/fleetCostTrailerStanding';
import ConfigMetaBadge from '../config/ConfigMetaBadge';
import NetworkChargeMonthsTable from './NetworkChargeMonthsTable';
import CostCheckMonthsTable from './CostCheckMonthsTable';
import { ConfigChangeHistoryButton } from '../config/ConfigChangeHistoryPanel';

export interface ProfitabilityAssumptionsPanelProps {
  visible: boolean;
  onClose?: () => void;
  /** Called after a successful save (e.g. prompt user to recompute). */
  onSaved?: () => void;
  className?: string;
}

export default function ProfitabilityAssumptionsPanel({
  visible,
  onClose,
  onSaved,
  className = '',
}: ProfitabilityAssumptionsPanelProps) {
  const [configData, setConfigData] = useState<ProfitabilityAssumptionsConfig | null>(null);
  const [configDraft, setConfigDraft] = useState<ProfitabilityAssumptionsConfig | null>(null);
  const [fleetCostConfig, setFleetCostConfig] = useState<FleetCostAssumptionsConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configEditMode, setConfigEditMode] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [assetArticQty, setAssetArticQty] = useState(0);
  const [assetTrailerQty, setAssetTrailerQty] = useState(0);

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const [data, fleet, labels] = await Promise.all([
        fetchTruthProfitabilityConfig(),
        fetchFleetCostAssumptionsConfig().catch(() => null),
        fetchFleetCostAssetClassLabels().catch(() => null),
      ]);
      setConfigData(data);
      setConfigDraft(data ? JSON.parse(JSON.stringify(data)) : null);
      setFleetCostConfig(fleet);
      if (labels) {
        setAssetArticQty(
          countFleetCostQtyByName({
            assetClassLabels: labels,
            rateBasis: 'tonnage',
            isEmergency: false,
            rowNames: ['Artic'],
          }).Artic ?? 0,
        );
        setAssetTrailerQty(countTrailerAssets(labels));
      } else {
        setAssetArticQty(0);
        setAssetTrailerQty(0);
      }
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to load assumptions');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  /** Fleet config with trailer qty prefilled so Artic Standing matches Configuration. */
  const fleetCostForStanding = useMemo(() => {
    if (!fleetCostConfig) return null;
    return withTrailerStandingAssetPrefill(fleetCostConfig, {
      articQty: assetArticQty,
      trailerQty: assetTrailerQty,
    });
  }, [fleetCostConfig, assetArticQty, assetTrailerQty]);

  useEffect(() => {
    if (visible && !configData && !configLoading) {
      void fetchConfig();
    }
  }, [visible, configData, configLoading, fetchConfig]);

  useEffect(() => {
    if (!visible) return;
    const onUpdated = () => {
      // Don't clobber an in-progress Costs edit from a Running Costs save.
      setConfigEditMode((editing) => {
        if (!editing) void fetchConfig();
        return editing;
      });
    };
    window.addEventListener('zecure:profitability-config-updated', onUpdated);
    return () => window.removeEventListener('zecure:profitability-config-updated', onUpdated);
  }, [visible, fetchConfig]);

  const saveConfig = useCallback(async () => {
    if (!configDraft) return;
    setConfigSaving(true);
    setConfigError(null);
    try {
      const actor =
        readSessionUserEmail()
        || (await getCurrentUserEmail())?.trim()
        || 'user';
      // Standing £/day is owned by Fleet Cost Standing Total — stamp derived
      // values so we never persist a divergent manual Standing column.
      let payload: ProfitabilityAssumptionsConfig = {
        ...configDraft,
        updated_by: actor,
        updated_at: new Date().toISOString(),
      };
      if (fleetCostForStanding) {
        const derived = deriveStandingPerDayMaps(fleetCostForStanding);
        payload = {
          ...payload,
          depreciation: {
            ...payload.depreciation,
            ...derived.body_style,
          } as ProfitabilityAssumptionsConfig['depreciation'],
          depreciation_tonnage: {
            ...(payload.depreciation_tonnage ?? {}),
            ...derived.tonnage,
          } as ProfitabilityAssumptionsConfig['depreciation_tonnage'],
        };
      }
      const saved = await saveTruthProfitabilityConfig(payload);
      setConfigData(saved);
      setConfigDraft(JSON.parse(JSON.stringify(saved)));
      setConfigEditMode(false);
      window.dispatchEvent(new CustomEvent('zecure:profitability-config-updated'));
      onSaved?.();
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to save assumptions');
    } finally {
      setConfigSaving(false);
    }
  }, [configDraft, fleetCostForStanding, onSaved]);

  const beginEdit = () => {
    if (configEditMode) return;
    setConfigDraft(configData ? JSON.parse(JSON.stringify(configData)) : null);
    setConfigEditMode(true);
  };

  const cancelEdit = () => {
    setConfigDraft(configData ? JSON.parse(JSON.stringify(configData)) : null);
    setConfigEditMode(false);
  };

  const costBasis: ProfitabilityCostRateBasis = configDraft?.cost_rate_basis ?? 'body_style';
  const rateKeys =
    costBasis === 'tonnage' ? PROFITABILITY_TONNAGE_CLASSES : PROFITABILITY_VEHICLE_TYPES;

  const fuelMap =
    costBasis === 'tonnage'
      ? (configDraft?.fuel_cost_tonnage ?? {})
      : (configDraft?.fuel_cost ?? {});
  const storedDepMap =
    costBasis === 'tonnage'
      ? (configDraft?.depreciation_tonnage ?? {})
      : (configDraft?.depreciation ?? {});
  const standingBreakdownByKey = useMemo(() => {
    if (!fleetCostForStanding) return null;
    const trailer = trailerStandingFromConfig(fleetCostForStanding);
    const vehicleOnlyConfig = {
      ...fleetCostForStanding,
      trailer_standing: { ...trailer, allocation: 'none' as const },
    };
    const vehicleMaps = deriveStandingPerDayMaps(vehicleOnlyConfig);
    const withTrailerMaps = deriveStandingPerDayMaps(fleetCostForStanding);
    const vehicleMap = costBasis === 'tonnage' ? vehicleMaps.tonnage : vehicleMaps.body_style;
    const totalMap = costBasis === 'tonnage' ? withTrailerMaps.tonnage : withTrailerMaps.body_style;
    const addOn = trailerStandingAddOnPerDayGbp(trailer);
    const parts: Record<string, { vehicle: number; trailer: number; total: number }> = {};
    const keys = new Set([...Object.keys(vehicleMap), ...Object.keys(totalMap)]);
    for (const key of keys) {
      const vehicle = vehicleMap[key] ?? 0;
      const getsTrailer =
        addOn > 0
        && (
          (costBasis === 'tonnage' && key === trailer.allocate_to_tonnage_class)
          || (costBasis === 'body_style' && key === TRAILER_ALLOCATION_BODY_STYLE_PEER)
        );
      const trailerPart = getsTrailer ? addOn : 0;
      parts[key] = {
        vehicle,
        trailer: trailerPart,
        total: totalMap[key] ?? Math.round((vehicle + trailerPart) * 100) / 100,
      };
    }
    return parts;
  }, [fleetCostForStanding, costBasis]);

  const standingPerDayByKey = useMemo(() => {
    if (!standingBreakdownByKey) return null;
    const out: Record<string, number> = {};
    for (const [key, p] of Object.entries(standingBreakdownByKey)) {
      out[key] = p.total;
    }
    return out;
  }, [standingBreakdownByKey]);

  const driverMap =
    costBasis === 'tonnage'
      ? (configDraft?.driver_cost_tonnage ?? {})
      : (configDraft?.driver_cost ?? {});

  const setFuelRate = (key: string, value: number) => {
    setConfigDraft((prev) => {
      if (!prev) return prev;
      if (costBasis === 'tonnage') {
        return {
          ...prev,
          fuel_cost_tonnage: {
            ...(prev.fuel_cost_tonnage ?? ({} as Record<ProfitabilityTonnageClass, number>)),
            [key as ProfitabilityTonnageClass]: value,
          },
        };
      }
      return {
        ...prev,
        fuel_cost: { ...prev.fuel_cost, [key as ProfitabilityVehicleType]: value },
      };
    });
  };

  const setDriverRate = (
    key: string,
    field: 'mileage_rate' | 'driving_hourly_rate' | 'waiting_hourly_rate',
    value: number,
  ) => {
    setConfigDraft((prev) => {
      if (!prev) return prev;
      if (costBasis === 'tonnage') {
        const current = prev.driver_cost_tonnage?.[key as ProfitabilityTonnageClass] ?? {
          mileage_rate: 0,
          driving_hourly_rate: 0,
          waiting_hourly_rate: 0,
        };
        return {
          ...prev,
          driver_cost_tonnage: {
            ...(prev.driver_cost_tonnage ??
              ({} as Record<ProfitabilityTonnageClass, typeof current>)),
            [key as ProfitabilityTonnageClass]: { ...current, [field]: value },
          },
        };
      }
      const current = prev.driver_cost[key as ProfitabilityVehicleType] ?? {
        mileage_rate: 0,
        driving_hourly_rate: 0,
        waiting_hourly_rate: 0,
      };
      return {
        ...prev,
        driver_cost: {
          ...prev.driver_cost,
          [key as ProfitabilityVehicleType]: { ...current, [field]: value },
        },
      };
    });
  };

  if (!visible) return null;

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-secondary/50 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <Settings className="w-3.5 h-3.5 text-warning shrink-0" />
          <span className="text-xs font-semibold text-foreground">Profitability Assumptions</span>
          {configData ? (
            <ConfigMetaBadge variant="version" version={configData.version} className="ml-2" />
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!configEditMode ? (
            <Button
              type="button"
              variant="warning"
              onClick={() => beginEdit()}
              disabled={!configDraft}
              className="h-auto px-2.5 py-1 text-xs text-white disabled:bg-muted"
            >
              Edit All
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="default"
                onClick={() => void saveConfig()}
                disabled={configSaving}
                className="h-auto px-2.5 py-1 text-xs disabled:bg-muted disabled:opacity-100"
              >
                <Save className="w-3 h-3" />
                {configSaving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={cancelEdit}
                className="h-auto px-2.5 py-1 text-xs"
              >
                Cancel
              </Button>
            </>
          )}
          {onClose && (
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={onClose}
              className="p-1"
              aria-label="Close assumptions panel"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {configError && (
        <div className="px-4 py-2 text-xs text-destructive bg-destructive/20 border-b border-destructive/20">
          {configError}
        </div>
      )}

      {configLoading ? (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground">Loading config…</div>
      ) : configDraft ? (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Apply cost rates by
              </label>
              {configEditMode ? (
                <select
                  value={configDraft.cost_rate_basis ?? 'body_style'}
                  onChange={(e) =>
                    setConfigDraft((prev) =>
                      prev
                        ? {
                            ...prev,
                            cost_rate_basis: e.target.value as ProfitabilityCostRateBasis,
                          }
                        : prev,
                    )
                  }
                  className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground focus:ring-warning focus:border-warning"
                >
                  <option value="body_style">Body style (Lorry, Tractor Unit, …)</option>
                  <option value="tonnage">Tonnage class (3.5T, 7.5T, 18T, 26T, Artic)</option>
                </select>
              ) : (
                <div className="w-full text-left bg-background/40 border border-border rounded px-2 py-1.5 text-xs text-foreground">
                  {(configDraft.cost_rate_basis ?? 'body_style') === 'tonnage'
                    ? 'Tonnage class (3.5T, 7.5T, 18T, 26T, Artic)'
                    : 'Body style (Lorry, Tractor Unit, …)'}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Must match how vehicles are classified in Assets → Vehicle Details.
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Fuel source</label>
              {configEditMode ? (
                <select
                  value={configDraft.fuel_source ?? 'mileage_rates'}
                  onChange={(e) =>
                    setConfigDraft((prev) =>
                      prev
                        ? { ...prev, fuel_source: e.target.value as ProfitabilityFuelSource }
                        : prev,
                    )
                  }
                  className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground focus:ring-warning focus:border-warning"
                >
                  <option value="mileage_rates">Mileage rates (£/mi from assumptions)</option>
                  <option value="jigsaw">Fuel transactions</option>
                </select>
              ) : (
                <div className="w-full text-left bg-background/40 border border-border rounded px-2 py-1.5 text-xs text-foreground">
                  {(configDraft.fuel_source ?? 'mileage_rates') === 'jigsaw'
                    ? 'Fuel transactions'
                    : 'Mileage rates (£/mi from assumptions)'}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                How fuel cost is derived at compute time when fuel data is missing or ignored.
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Standing cost policy
              </label>
              {configEditMode ? (
                <select
                  value={configDraft.standing_cost_policy ?? 'calendar_day'}
                  onChange={(e) =>
                    setConfigDraft((prev) =>
                      prev
                        ? {
                            ...prev,
                            standing_cost_policy: e.target.value as ProfitabilityStandingCostPolicy,
                          }
                        : prev,
                    )
                  }
                  className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground focus:ring-warning focus:border-warning"
                >
                  <option value="calendar_day">Every calendar day in range</option>
                  <option value="active_day">Active days only (mileage or revenue)</option>
                </select>
              ) : (
                <div className="w-full text-left bg-background/40 border border-border rounded px-2 py-1.5 text-xs text-foreground">
                  {(configDraft.standing_cost_policy ?? 'calendar_day') === 'active_day'
                    ? 'Active days only (mileage or revenue)'
                    : 'Every calendar day in range'}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                When standing / depreciation is charged per vehicle-day.
              </p>
            </div>
          </div>

          <NetworkChargeMonthsTable />

          <CostCheckMonthsTable />

          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">
              Cost rates by {costBasis === 'tonnage' ? 'tonnage class' : 'body style'}
            </h4>
            <p className="text-xs text-muted-foreground mb-2">
              Fuel £/mi is the fallback when fuel data is absent.
              {configEditMode
                ? ' Edit fuel/driver rates below, then Save.'
                : ' Use Edit All to change fuel/driver rates.'}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-160 border-separate border-spacing-0">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="sticky left-0 z-20 text-left py-2 px-3 font-medium bg-card border-b border-r border-border shadow-panel">
                      {costBasis === 'tonnage' ? 'Tonnage class' : 'Body style'}
                    </th>
                    <th className="text-right py-2 px-2 font-medium border-b border-border">Fuel (£/mi)</th>
                    <th className="text-right py-2 px-2 font-medium border-b border-border">Driver (£/mi)</th>
                    <th className="text-right py-2 px-2 font-medium border-b border-border">Driving (£/hr)</th>
                    <th className="text-right py-2 px-2 font-medium border-b border-border">Waiting (£/hr)</th>
                    <th className="text-right py-2 px-2 font-medium border-b border-border">
                      <span className="inline-flex items-center justify-end gap-1">
                        Standing (£/day)
                        <span className="relative inline-flex group/tip align-middle">
                          <CircleHelp
                            className="w-3.5 h-3.5 text-muted-foreground group-hover/tip:text-success cursor-help transition-colors"
                            aria-hidden
                          />
                          <span className="sr-only">
                            Standing £/day is Fleet Cost Standing Total divided by 365
                          </span>
                          <div
                            role="tooltip"
                            className="invisible group-hover/tip:visible absolute right-0 top-full mt-2 z-30 w-64 text-left bg-popover text-foreground text-xs font-normal normal-case tracking-normal rounded p-3 border border-border shadow-lg pointer-events-none"
                          >
                            <p className="font-semibold text-success mb-1.5">Standing (£/day)</p>
                            <p className="text-foreground leading-relaxed">
                              Fleet Cost Assumptions → Standing Costs Total (£/yr/veh) ÷ 365.
                            </p>
                            <p className="text-foreground leading-relaxed mt-1">
                              For Artic: vehicle standing + trailer pool share (shown as two sums).
                            </p>
                            <p className="text-foreground leading-relaxed mt-1">
                              Read-only — edit standing line items and the trailer pool under Fleet
                              Cost Assumptions → Standing Costs.
                            </p>
                          </div>
                        </span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {rateKeys.map((vt) => {
                    const rate = (driverMap as Record<string, {
                      mileage_rate?: number;
                      driving_hourly_rate?: number;
                      waiting_hourly_rate?: number;
                    }>)[vt];
                    const inputClass =
                      'w-20 bg-background border border-border rounded px-2 py-0.5 text-xs text-right text-foreground focus:ring-warning focus:border-warning';
                    const editable = (
                      value: number,
                      onChange: (n: number) => void,
                      step: string,
                    ) =>
                      configEditMode ? (
                        <input
                          type="number"
                          step={step}
                          min="0"
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                          className={inputClass}
                        />
                      ) : (
                        <span className="tabular-nums text-foreground">{value}</span>
                      );
                    return (
                      <tr key={`rate-${vt}`} className="hover:bg-secondary/30 group">
                        <td className="sticky left-0 z-10 py-2 px-3 text-foreground font-medium bg-card border-r border-border group-hover:bg-secondary shadow-panel">
                          {vt}
                        </td>
                        <td className="py-2 px-2 text-right">
                          {editable(
                            (fuelMap as Record<string, number>)[vt] ?? 0,
                            (n) => setFuelRate(vt, n),
                            '0.01',
                          )}
                        </td>
                        <td className="py-2 px-2 text-right">
                          {editable(
                            rate?.mileage_rate ?? 0,
                            (n) => setDriverRate(vt, 'mileage_rate', n),
                            '0.01',
                          )}
                        </td>
                        <td className="py-2 px-2 text-right">
                          {editable(
                            rate?.driving_hourly_rate ?? 0,
                            (n) => setDriverRate(vt, 'driving_hourly_rate', n),
                            '0.01',
                          )}
                        </td>
                        <td className="py-2 px-2 text-right">
                          {editable(
                            rate?.waiting_hourly_rate ?? 0,
                            (n) => setDriverRate(vt, 'waiting_hourly_rate', n),
                            '0.01',
                          )}
                        </td>
                        <td className="py-2 px-2 text-right">
                          <span
                            className="tabular-nums text-muted-foreground"
                            title="From Fleet Cost Standing Total ÷ 365 — edit under Fleet Cost Assumptions → Standing Costs"
                          >
                            {(() => {
                              const parts = standingBreakdownByKey?.[vt];
                              if (parts && parts.trailer > 0) {
                                return (
                                  <span title={`Vehicle £${parts.vehicle.toFixed(2)}/day + trailer £${parts.trailer.toFixed(2)}/day`}>
                                    {parts.vehicle.toFixed(2)}
                                    <span className="text-muted-foreground"> + </span>
                                    <span className="text-warning/90">{parts.trailer.toFixed(2)}</span>
                                  </span>
                                );
                              }
                              if (standingPerDayByKey) {
                                return (standingPerDayByKey[vt] ?? 0).toFixed(2);
                              }
                              const stored = (storedDepMap as Record<string, number>)[vt] ?? 0;
                              return typeof stored === 'number' ? stored.toFixed(2) : stored;
                            })()}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {configEditMode && (
            <p className="text-xs text-warning/70 italic">
              Changes take effect on the next profitability computation run. Existing stored results
              are not retroactively updated — use Compute above after saving.
            </p>
          )}
        </div>
      ) : (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground space-y-2">
          <p>No config available.</p>
          <p className="text-xs text-accent">
            Run{' '}
            <code className="text-muted-foreground">npm run seed:profitability-config -- --org org123</code>{' '}
            in zeecore, or save defaults from zeecore API.
          </p>
        </div>
      )}

      <div className="px-4 py-2 border-t border-border/80 bg-background/40 rounded-b-lg flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          {configData && (configData.updated_at || configData.updated_by) ? (
            <ConfigMetaBadge
              variant="provenance"
              updatedAt={configData.updated_at}
              updatedBy={configData.updated_by}
            />
          ) : (
            <p className="text-xs text-muted-foreground">No saves recorded yet</p>
          )}
        </div>
        <ConfigChangeHistoryButton kind="profitability" />
      </div>
    </div>
  );
}
